import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IEntityReducer } from '../../../reducers/entities';
import { IActionEntity, IActionEntityBasic, IActionEntityDAO } from 'types/domain/action';
import { IPosition, IRotation } from 'types/domain/base';
import { IMediaAsset } from 'types/domain/media';
import { getEntities } from '../../utils/getEntities';
import { PortraitState } from 'core/slices/player';
import { createCachedSelector } from 're-reselect';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => id || state.state[EntityTypes.ACTION_ENTITY];
const portrait = (state: RootState) => state.core.player.state.portrait;

export const selectActionEntity = createSelector(
  [entity, id, portrait],
  (entity: IEntityReducer, id: number, portrait: PortraitState) => {
    const actionEntity: IActionEntity = entity[EntityTypes.ACTION_ENTITY][id];

    return actionEntity
      ? ({
          ...actionEntity,
          text: actionEntity.textId ? entity[EntityTypes.TEXT][actionEntity.textId] : null,
          audio: actionEntity.audioId ? entity[EntityTypes.AUDIO][actionEntity.audioId] : null,
          image: actionEntity.imageId ? entity[EntityTypes.IMAGE][actionEntity.imageId] : null,
          video: actionEntity.videoId ? entity[EntityTypes.VIDEO][actionEntity.videoId] : null,
          model: actionEntity.modelId ? entity[EntityTypes.MODEL][actionEntity.modelId] : null,
          animations: getEntities(entity, EntityTypes.ANIMATION, 'actionId', id),
          mediaAsset: actionEntity.mediaAssetId
            ? (entity[EntityTypes.MEDIA_ASSET][actionEntity.mediaAssetId] as IMediaAsset)
            : null,
          position:
            (entity[EntityTypes.POSITION][
              portrait ? actionEntity.pPositionId || actionEntity.positionId : actionEntity.positionId
            ] as IPosition) || actionEntity.position,
          referencePosition: actionEntity.referencePositionId
            ? (entity[EntityTypes.POSITION][actionEntity.referencePositionId] as IPosition)
            : null,
          rotation: entity[EntityTypes.ROTATION][
            portrait ? actionEntity.pRotationId || actionEntity.rotationId : actionEntity.rotationId
          ] as IRotation,
        } as IActionEntityDAO)
      : null;
  },
);

export const selectActionEntityBasic = createCachedSelector(
  [entity, id, portrait],
  (entity: IEntityReducer, id: number, portrait: PortraitState) => {
    const actionEntity: IActionEntity = entity[EntityTypes.ACTION_ENTITY][id];

    return actionEntity
      ? ({
          ...actionEntity,
          audio: actionEntity.audioId ? entity[EntityTypes.AUDIO][actionEntity.audioId] : null,
          image: actionEntity.imageId ? entity[EntityTypes.IMAGE][actionEntity.imageId] : null,
          video: actionEntity.videoId ? entity[EntityTypes.VIDEO][actionEntity.videoId] : null,
          model: actionEntity.modelId ? entity[EntityTypes.MODEL][actionEntity.modelId] : null,
          animations: getEntities(entity, EntityTypes.ANIMATION, 'actionId', id),
          mediaAsset: actionEntity.mediaAssetId
            ? (entity[EntityTypes.MEDIA_ASSET][actionEntity.mediaAssetId] as IMediaAsset)
            : null,
          position:
            (entity[EntityTypes.POSITION][
              portrait ? actionEntity.pPositionId || actionEntity.positionId : actionEntity.positionId
            ] as IPosition) || actionEntity.position,
          referencePosition: actionEntity.referencePositionId
            ? (entity[EntityTypes.POSITION][actionEntity.referencePositionId] as IPosition)
            : null,
          rotation: entity[EntityTypes.ROTATION][
            portrait ? actionEntity.pRotationId || actionEntity.rotationId : actionEntity.rotationId
          ] as IRotation,
        } as IActionEntityDAO)
      : null;
  },
)((state, id) => id || state.state[EntityTypes.ACTION_ENTITY] || 'unknown');

export function parseActionEntityBasic(entity, id) {
  const actionEntity: IActionEntity = entity[EntityTypes.ACTION_ENTITY][id];

  return actionEntity
    ? ({
        ...actionEntity,
        text: actionEntity.textId ? entity[EntityTypes.TEXT][actionEntity.textId] : null,
        audio: actionEntity.audioId ? entity[EntityTypes.AUDIO][actionEntity.audioId] : null,
        image: actionEntity.imageId ? entity[EntityTypes.IMAGE][actionEntity.imageId] : null,
        video: actionEntity.videoId ? entity[EntityTypes.VIDEO][actionEntity.videoId] : null,
        model: actionEntity.modelId ? entity[EntityTypes.MODEL][actionEntity.modelId] : null,
        animations: getEntities(entity, EntityTypes.ANIMATION, 'actionId', id),
        mediaAsset: actionEntity.mediaAssetId
          ? (entity[EntityTypes.MEDIA_ASSET][actionEntity.mediaAssetId] as IMediaAsset)
          : null,
      } as IActionEntityBasic)
    : null;
}
